import { useState } from 'react'
import { Link } from 'react-router-dom'

import {
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUS_LABELS,
  OBJECTIVE_LABELS,
  sumMetrics,
  type CampaignListItem,
  type CampaignStatus,
} from '@/api/campaigns'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import {
  Card,
  DataTable,
  EmptyState,
  formatCurrency,
  formatInteger,
  formatRatio,
  Icon,
  LoadingBlock,
  type Column,
  type FormatOptions,
} from '@/design'
import { usePermissions } from '@/lib/access'
import { formatDateKey } from '@/lib/filters'
import { usePageFreshness } from '@/lib/freshness'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { CampaignStatusBadge, formatDateRange } from './components'
import { useCampaigns, useMoney } from './hooks'
import './campaigns.css'

const unknown = <span className="camp-muted">—</span>

function columns(money: FormatOptions): Column<CampaignListItem>[] {
  return [
    {
      key: 'name',
      header: 'Campaign',
      sortValue: (row) => row.campaign.name,
      cell: (row) => (
        <span className="camp-cell-stack">
          <span>{row.campaign.name}</span>
          <span className="camp-cell-sub">{row.campaign.customObjective ?? OBJECTIVE_LABELS[row.campaign.objective]}</span>
        </span>
      ),
    },
    { key: 'status', header: 'Status', sortValue: (row) => row.campaign.status, cell: (row) => <CampaignStatusBadge status={row.campaign.status} /> },
    {
      key: 'channels',
      header: 'Channels',
      sortValue: (row) => row.channels.length,
      cell: (row) => {
        if (row.channelsUnavailable) return unknown
        const offline = row.channels.filter((channel) => channel.kind === 'offline').length
        return (
          <span className="camp-cell-stack">
            <span>{formatInteger(row.channels.length)}</span>
            <span className="camp-cell-sub">
              {row.channels.length - offline} digital · {offline} offline
            </span>
          </span>
        )
      },
    },
    {
      key: 'dates',
      header: 'Dates',
      sortValue: (row) => row.campaign.startsAt ?? '',
      cell: (row) => formatDateRange(row.campaign.startsAt, row.campaign.endsAt, (key) => formatDateKey(key, true)),
    },
    {
      key: 'budget',
      header: 'Budget',
      align: 'end',
      sortValue: (row) => row.campaign.totalBudget ?? -1,
      cell: (row) => (row.campaign.totalBudget === null ? <span className="camp-muted">—</span> : formatCurrency(row.campaign.totalBudget, money)),
    },
    { key: 'revenue', header: 'Revenue', align: 'end', sortValue: (row) => row.totals.revenue, cell: (row) => (row.performanceUnavailable ? unknown : formatCurrency(row.totals.revenue, money)) },
    { key: 'entries', header: 'Entries', align: 'end', sortValue: (row) => row.totals.entries, cell: (row) => (row.performanceUnavailable ? unknown : formatInteger(row.totals.entries)) },
    {
      key: 'roas',
      header: 'ROAS',
      align: 'end',
      sortValue: (row) => row.totals.returnOnAdSpend ?? -1,
      cell: (row) => (row.performanceUnavailable ? unknown : row.totals.returnOnAdSpend === null ? <span className="camp-muted">No spend</span> : formatRatio(row.totals.returnOnAdSpend)),
    },
  ]
}

type StatusFilter = 'all' | CampaignStatus

/** Every campaign, online and offline channels together, with what each returned in the selected period. */
export function CampaignsListPage({ route }: { route: RouteDef }) {
  const campaigns = useCampaigns()
  usePageFreshness(campaigns.data?.meta)
  const access = usePermissions()
  const search = useFilterSearch()
  const money = useMoney()
  const [status, setStatus] = useState<StatusFilter>('all')

  const all = campaigns.data?.data ?? []
  const rows = status === 'all' ? all : all.filter((row) => row.campaign.status === status)
  const totals = sumMetrics(all.filter((row) => !row.performanceUnavailable).map((row) => row.totals))
  const unavailable = all.filter((row) => row.performanceUnavailable).length
  const period = campaigns.data?.meta.period?.label

  return (
    <SectionFrame
      route={route}
      actions={
        access.can('campaigns.manage') ? (
          <Link className="ci-button" data-variant="primary" to={{ pathname: '/marketing/campaigns/new', search }}>
            <Icon name="sparkle" size={14} />
            New campaign
          </Link>
        ) : null
      }
    >
      <Card
        title="Campaigns"
        subtitle={period ? `Performance for ${period.toLowerCase()}` : undefined}
        busy={campaigns.isFetching}
        actions={
          <label className="camp-inline-select">
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value as StatusFilter)}>
              <option value="all">All statuses</option>
              {CAMPAIGN_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {CAMPAIGN_STATUS_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
        }
      >
        {campaigns.isPending ? <LoadingBlock rows={5} /> : null}
        {campaigns.isError ? <EmptyState variant="outage" compact description="Campaigns could not be loaded." /> : null}
        {campaigns.data ? (
          <>
            {all.length > 0 ? (
              <dl className="camp-mix" aria-label="All campaigns">
                <div>
                  <dt>Active</dt>
                  <dd>{formatInteger(all.filter((row) => row.campaign.status === 'active').length)}</dd>
                </div>
                <div>
                  <dt>Revenue</dt>
                  <dd>{formatCurrency(totals.revenue, money)}</dd>
                </div>
                <div>
                  <dt>Entries</dt>
                  <dd>{formatInteger(totals.entries)}</dd>
                </div>
                <div>
                  <dt>Spend</dt>
                  <dd>{totals.spend === null ? '—' : formatCurrency(totals.spend, money)}</dd>
                </div>
                <div>
                  <dt>Blended ROAS</dt>
                  <dd>{totals.returnOnAdSpend === null ? '—' : formatRatio(totals.returnOnAdSpend)}</dd>
                </div>
              </dl>
            ) : null}
            <DataTable
              caption="Campaigns"
              columns={columns(money)}
              rows={rows}
              rowKey={(row) => row.campaign.id}
              rowHref={(row) => `/marketing/campaigns/${encodeURIComponent(row.campaign.id)}${search}`}
              initialSort={{ key: 'revenue', direction: 'desc' }}
              empty={
                all.length === 0 ? (
                  <EmptyState
                    variant="no-data"
                    compact
                    title="No campaigns yet"
                    description="Create a campaign to plan its channels, online and offline, and track each one."
                  />
                ) : (
                  <EmptyState variant="no-results" compact title="No campaigns with this status" description="Choose another status." />
                )
              }
            />
            {unavailable > 0 ? (
              <p className="camp-muted" role="status">
                Figures for {unavailable === 1 ? '1 campaign' : `${unavailable} campaigns`} could not be loaded and are left out of the totals above.
              </p>
            ) : null}
          </>
        ) : null}
      </Card>
    </SectionFrame>
  )
}
